/**
 * PaletteDock.ts — the bottom glass tray of the Painting scene.
 *
 * Row of color SWATCHES (paint-by-number): each shows its number, a per-color
 * progress RING (teal as it fills), and an owned/buyable state. Selecting a
 * buyable color reveals the Buy CTA + stake stepper. A scrollable strip handles
 * the 16-color picture. Includes the Autoplay entry button.
 */
import { Container, Graphics } from 'pixi.js';
import { Button, drawRing, glassPanel, makeText } from './widgets';
import theme, { hex, shade } from '../render/theme';
import { stakeLabel } from '../util/format';
import type { Viewport } from '../app/AppContext';
import type { ColorState } from '../state/GameStore';
import { Juice } from '../fx/Juice';

export interface PaletteDockCallbacks {
  onSelectColor: (colorId: number) => void;
  onBuy: (colorId: number, stake: number) => void;
  onStakeChange: (stake: number) => void;
  onAutoplay: () => void;
}

class Swatch extends Container {
  readonly colorId: number;
  private disc = new Graphics();
  private ring = new Graphics();
  private numberText;
  private check;
  private selectRing = new Graphics();
  private baseHex: string;
  selected = false;

  constructor(public state: ColorState, private onTap: (id: number) => void) {
    super();
    this.colorId = state.colorId;
    this.baseHex = state.hex;
    this.numberText = makeText(String(state.number), {
      size: 18,
      weight: '800',
      color: theme.color.white,
    });
    this.numberText.anchor.set(0.5);
    this.check = makeText('✓', { size: 20, weight: '800', color: theme.color.white });
    this.check.anchor.set(0.5);
    this.check.visible = false;
    this.addChild(this.selectRing, this.ring, this.disc, this.numberText, this.check);
    this.eventMode = 'static';
    this.cursor = 'pointer';
    this.on('pointertap', () => this.onTap(this.colorId));
    this.redraw();
  }

  setSelected(v: boolean): void {
    if (this.selected === v) return;
    this.selected = v;
    this.redraw();
    if (v) Juice.squash(this, 0.18, theme.motion.base);
  }

  refresh(state: ColorState): void {
    this.state = state;
    this.redraw();
  }

  private redraw(): void {
    const r = 26;
    const c = this.state;
    this.disc.clear();
    const owned = c.bought;
    const baseColor = hex(this.baseHex);
    // disc
    this.disc.circle(0, 0, r).fill(baseColor);
    this.disc.circle(0, 0, r).stroke({ width: 2, color: hex(shade(this.baseHex, -0.25)), alpha: 0.6 });
    // dim if not bought (fresh) or strongly desaturate if completed (already done)
    if (c.completed) {
      this.disc.circle(0, 0, r).fill({ color: theme.color.white, alpha: 0.55 });
    } else if (!owned) {
      this.disc.circle(0, 0, r).fill({ color: theme.color.white, alpha: 0.28 });
    }

    // progress ring (full gold ring on completed colours so they read as "done")
    const prog = c.cellCount > 0 ? c.revealed / c.cellCount : 0;
    drawRing(
      this.ring,
      0,
      0,
      r + 7,
      4,
      owned ? (c.completed ? 1 : prog) : 0,
      c.completed ? hex(theme.palette.gold2) : hex(theme.palette.teal),
      theme.color.hint,
      owned ? 0.25 : 0.0,
    );

    // selection ring: an outer glow + a tight teal ring on the active swatch. The active swatch
    // also scales up slightly (handled below) so the picked colour visibly "lifts" from the row.
    this.selectRing.clear();
    if (this.selected) {
      // outer soft glow disc behind everything
      this.selectRing.circle(0, 0, r + 18).fill({ color: hex(theme.palette.teal), alpha: 0.18 });
      this.selectRing.circle(0, 0, r + 13).stroke({ width: 3, color: hex(theme.palette.teal), alpha: 0.95 });
    }
    this.scale.set(this.selected ? 1.12 : c.completed ? 0.94 : 1);

    // number vs check
    const numColor = isLight(this.baseHex) ? theme.color.ink : theme.color.white;
    this.numberText.style.fill = numColor;
    this.check.style.fill = numColor;
    this.numberText.visible = !c.completed;
    this.check.visible = c.completed;
    this.numberText.alpha = owned ? 1 : 0.85;
    if (c.completed) this.check.alpha = 0.92;
  }
}

export class PaletteDock extends Container {
  private panel: Graphics;
  private strip = new Container();
  private stripMask = new Graphics();
  private swatches: Swatch[] = [];
  private cb: PaletteDockCallbacks;

  // action row
  private buyBtn: Button;
  private autoplayBtn: Button;
  private stepperMinus: Button;
  private stepperPlus: Button;
  private stakeText;
  private stakeBg = new Graphics();
  private infoText;

  private betLevels: number[];
  private stakeIdx = 3;
  private selectedColorId: number | null = null;

  private w = 0;
  private h = 0;
  private stripScroll = 0;
  private stripContentW = 0;
  private dragging = false;
  private dragStartX = 0;
  private dragStartScroll = 0;

  constructor(
    colors: ColorState[],
    betLevels: number[],
    initialStakeIdx: number,
    cb: PaletteDockCallbacks,
  ) {
    super();
    this.cb = cb;
    this.betLevels = betLevels;
    this.stakeIdx = Math.max(0, Math.min(betLevels.length - 1, initialStakeIdx));

    this.panel = glassPanel(300, 200);
    this.addChild(this.panel);

    this.strip.addChild(this.stripMask);
    this.strip.mask = this.stripMask;
    this.addChild(this.strip);

    for (const c of colors) {
      const sw = new Swatch(c, (id) => this.handleSelect(id));
      this.swatches.push(sw);
      this.strip.addChild(sw);
    }

    // stake stepper
    this.stepperMinus = new Button({
      width: 40, height: 40, label: '–', fill: theme.color.white,
      textColor: theme.color.ink, fontSize: 26, radius: 20,
      onTap: () => this.changeStake(-1),
    });
    this.stepperPlus = new Button({
      width: 40, height: 40, label: '+', fill: theme.color.white,
      textColor: theme.color.ink, fontSize: 24, radius: 20,
      onTap: () => this.changeStake(1),
    });
    this.stakeText = makeText(stakeLabel(this.stake), { size: 18, weight: '800', color: theme.color.ink });
    this.stakeText.anchor.set(0.5);

    this.buyBtn = new Button({
      width: 200, height: 54, label: 'Buy color', icon: '🪙',
      fill: theme.color.gold2, textColor: theme.color.goldText, fontSize: 19,
      onTap: () => this.handleBuy(),
    });
    this.autoplayBtn = new Button({
      width: 54, height: 54, label: '∞', fill: theme.color.purple,
      textColor: theme.color.white, fontSize: 26, radius: 18,
      onTap: () => this.cb.onAutoplay(),
    });

    this.infoText = makeText('Pick a color to paint', { size: 13, weight: '700', color: theme.color.muted });
    this.infoText.anchor.set(0.5, 0);

    this.addChild(this.stakeBg, this.stepperMinus, this.stepperPlus, this.stakeText, this.buyBtn, this.autoplayBtn, this.infoText);

    this.setupStripDrag();
    this.updateActionRow();
  }

  get stake(): number {
    return this.betLevels[this.stakeIdx];
  }

  setStakeIndexForValue(v: number): void {
    const i = this.betLevels.indexOf(v);
    if (i >= 0) {
      this.stakeIdx = i;
      this.stakeText.text = stakeLabel(this.stake);
      this.updateActionRow();
    }
  }

  refreshColor(state: ColorState): void {
    const sw = this.swatches.find((s) => s.colorId === state.colorId);
    sw?.refresh(state);
    if (state.colorId === this.selectedColorId) this.updateActionRow();
  }

  private handleSelect(id: number): void {
    this.selectedColorId = id;
    for (const sw of this.swatches) sw.setSelected(sw.colorId === id);
    this.cb.onSelectColor(id);
    this.updateActionRow();
  }

  private selectedState(): ColorState | null {
    if (this.selectedColorId == null) return null;
    return this.swatches.find((s) => s.colorId === this.selectedColorId)?.state ?? null;
  }

  private changeStake(dir: number): void {
    const next = Math.max(0, Math.min(this.betLevels.length - 1, this.stakeIdx + dir));
    if (next === this.stakeIdx) return;
    this.stakeIdx = next;
    this.stakeText.text = stakeLabel(this.stake);
    Juice.squash(this.stakeText, 0.25, theme.motion.fast);
    this.cb.onStakeChange(this.stake);
    this.updateActionRow();
  }

  private handleBuy(): void {
    const st = this.selectedState();
    if (!st || st.bought) return;
    this.cb.onBuy(st.colorId, this.stake);
  }

  private updateActionRow(): void {
    const st = this.selectedState();
    const stepperEnabled = !!st && !st.bought;
    this.stepperMinus.setEnabled(stepperEnabled && this.stakeIdx > 0);
    this.stepperPlus.setEnabled(stepperEnabled && this.stakeIdx < this.betLevels.length - 1);
    this.stakeText.alpha = stepperEnabled ? 1 : 0.4;

    if (!st) {
      this.buyBtn.setEnabled(false);
      this.buyBtn.setLabel('Pick a color');
      this.infoText.text = 'Pick a color to paint';
    } else if (st.bought && !st.completed) {
      this.buyBtn.setEnabled(false);
      this.buyBtn.setLabel('Painting…');
      this.infoText.text = `Tap cells to reveal coins · ${st.revealed}/${st.cellCount}`;
    } else if (st.completed) {
      this.buyBtn.setEnabled(false);
      this.buyBtn.setLabel('Completed ✓');
      this.infoText.text = 'Color complete — pick another';
    } else {
      this.buyBtn.setEnabled(true);
      this.buyBtn.setLabel(`Buy · ${stakeLabel(this.stake)}`);
      this.infoText.text = `${st.cellCount} cells · color #${st.number}`;
    }
  }

  // ----- strip drag scroll -----
  private setupStripDrag(): void {
    this.strip.eventMode = 'static';
    this.strip.on('pointerdown', (e) => {
      this.dragging = true;
      this.dragStartX = e.global.x;
      this.dragStartScroll = this.stripScroll;
    });
    const up = () => (this.dragging = false);
    this.strip.on('pointerup', up);
    this.strip.on('pointerupoutside', up);
    this.strip.on('globalpointermove', (e) => {
      if (!this.dragging) return;
      const dx = e.global.x - this.dragStartX;
      const maxScroll = Math.max(0, this.stripContentW - this.w + 28);
      this.stripScroll = Math.max(-30, Math.min(maxScroll + 30, this.dragStartScroll - dx));
      this.layoutStrip();
    });
  }

  layout(v: Viewport): void {
    const pad = 14;
    this.w = v.width - pad * 2;
    this.h = 188 + v.insetBottom;
    const y = v.height - this.h - 8;
    this.position.set(pad, y);

    this.panel.clear();
    this.panel.roundRect(0, 0, this.w, this.h, theme.radius.card).fill({ color: theme.color.white, alpha: theme.color.glassAlpha });
    this.panel.roundRect(0, 0, this.w, this.h, theme.radius.card).stroke({ width: 1, color: theme.color.white, alpha: theme.color.glassStrokeAlpha });

    // strip mask region
    const stripY = 18;
    const stripH = 78;
    this.stripMask.clear();
    this.stripMask.roundRect(8, stripY, this.w - 16, stripH, 14).fill({ color: 0xffffff });
    this.layoutStrip();

    // info line
    this.infoText.position.set(this.w / 2, stripY + stripH + 6);

    // action row
    const rowY = stripY + stripH + 30;
    // stake stepper group on the left
    const stepW = 168;
    this.stakeBg.clear();
    this.stakeBg.roundRect(14, rowY, stepW, 44, 22).fill({ color: theme.color.bgLight3, alpha: 0.9 });
    this.stepperMinus.position.set(16, rowY + 2);
    this.stepperPlus.position.set(14 + stepW - 42, rowY + 2);
    this.stakeText.position.set(14 + stepW / 2, rowY + 22);

    // autoplay button on the far right
    this.autoplayBtn.resize(50, 50);
    this.autoplayBtn.position.set(this.w - 64, rowY - 3);

    // buy button fills the middle
    const buyX = 14 + stepW + 12;
    const buyW = this.w - buyX - 76;
    this.buyBtn.resize(buyW, 50);
    this.buyBtn.position.set(buyX, rowY - 3);
  }

  private layoutStrip(): void {
    const swR = 26;
    const gap = 26;
    const startX = 24;
    const cy = 18 + 38;
    let x = startX;
    for (const sw of this.swatches) {
      sw.position.set(x - this.stripScroll, cy);
      x += swR * 2 + gap;
    }
    this.stripContentW = x - startX;
  }

  /** Programmatically select a color (used after buy / autoplay). */
  selectColor(id: number): void {
    this.handleSelect(id);
  }

  setAutoplayActive(active: boolean): void {
    this.autoplayBtn.setFill(active ? theme.color.coral : theme.color.purple);
    this.autoplayBtn.setLabel(active ? '■' : '∞');
  }
}

function isLight(hexStr: string): boolean {
  const h = hexStr.replace('#', '');
  const r = parseInt(h.slice(0, 2), 16);
  const g = parseInt(h.slice(2, 4), 16);
  const b = parseInt(h.slice(4, 6), 16);
  const lum = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
  return lum > 0.62;
}
